/**
 * ConnectionEdge - React SVG component for connections in concentric layout
 *
 * Renders a line between two nodes with thickness based on evidence count
 * and color based on connection strength.
 */

import React from 'react';
import { EDGE_THICKNESS } from '../../constants/dashboardConfig';
import { getConnectionStrength } from '../../utils/networkUtils';

const STRENGTH_STYLES = {
  strong: { stroke: 'rgba(35, 163, 143, 0.25)', strokeWidth: 2.5 },
  medium: { stroke: 'rgba(0, 151, 178, 0.18)', strokeWidth: 1.8 },
  weak: { stroke: 'rgba(16, 3, 49, 0.08)', strokeWidth: 1.5 },
  highlighted: { stroke: '#23A38F', strokeWidth: 3, opacity: 0.6 },
};

function ConnectionEdge({
  edge,
  sourceNode,
  targetNode,
  isSelected,
  isHighlighted,
  onSelect,
}) {
  if (!sourceNode || !targetNode) return null;

  const evidenceCount = edge.evidence_count || edge.weight || 1;
  const strength = getConnectionStrength(evidenceCount);

  // Determine style
  let style;
  if (isSelected) {
    style = STRENGTH_STYLES.highlighted;
  } else if (isHighlighted) {
    style = { ...STRENGTH_STYLES[strength], stroke: '#23A38F', opacity: 0.4 };
  } else {
    style = STRENGTH_STYLES[strength];
  }

  // Scale thickness by evidence count within range
  const normalizedThickness = Math.min(
    EDGE_THICKNESS.max,
    Math.max(EDGE_THICKNESS.min, Math.log2(evidenceCount + 1) * 1.2)
  );

  const hitAreaWidth = Math.max(normalizedThickness + 10, 16);

  return (
    <g
      aria-label={`Connection between ${sourceNode.label || sourceNode.id} and ${targetNode.label || targetNode.id}, ${strength} strength`}
    >
      {/* Invisible wider hit area for click */}
      <line
        x1={sourceNode.x}
        y1={sourceNode.y}
        x2={targetNode.x}
        y2={targetNode.y}
        stroke="transparent"
        strokeWidth={hitAreaWidth}
        style={{ cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(edge);
        }}
      />

      {/* Visible edge */}
      <line
        x1={sourceNode.x}
        y1={sourceNode.y}
        x2={targetNode.x}
        y2={targetNode.y}
        stroke={style.stroke}
        strokeWidth={isSelected ? style.strokeWidth : normalizedThickness}
        opacity={style.opacity || 1}
        fill="none"
        style={{
          transition: 'all 0.4s',
          pointerEvents: 'none',
        }}
      />
    </g>
  );
}

export default ConnectionEdge;
